import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, ToastService } from '@cnt-workspace/data-access';
import { Role } from '@curbnturf/entities';
import { AlertController } from '@ionic/angular/standalone';
import { firstValueFrom } from 'rxjs';
import { AdminSessionState } from './admin-session-state';

/**
 * When the session runs out mid-task, asks the admin to sign in again over the
 * page they're on, so its URL and any unsaved edits stay. Wired in as
 * AuthService's SESSION_EXPIRED_HANDLER (see the admin app.config.ts).
 */
@Injectable({ providedIn: 'root' })
export class AdminSessionService {
  private readonly auth = inject(AuthService);
  private readonly alerts = inject(AlertController);
  private readonly router = inject(Router);
  private readonly toasts = inject(ToastService);
  private readonly state = inject(AdminSessionState);
  private asking = false;

  /** Several requests can fail at once: only one dialog opens. Never on the sign-in pages. */
  expired(): void {
    if (this.asking || this.router.url.startsWith('/auth')) return;
    this.asking = true;
    void this.ask().finally(() => (this.asking = false));
  }

  private async ask(): Promise<void> {
    const email = (await firstValueFrom(this.auth.authSession$))?.email ?? '';
    const alert = await this.alerts.create({
      header: 'Your session expired',
      message: 'Sign in again to keep working. Anything you haven’t saved is still on this page.',
      backdropDismiss: false,
      inputs: [
        { name: 'email', type: 'email', placeholder: 'Email', value: email },
        { name: 'password', type: 'password', placeholder: 'Password' },
      ],
      buttons: [
        { text: 'Go to the sign-in page', role: 'page' },
        {
          text: 'Sign in',
          role: 'confirm',
          // Returning false keeps the dialog open to show why it didn't work.
          handler: (values: { email?: string; password?: string }) => this.signIn(values, alert),
        },
      ],
    });
    this.state.setExpired(true);
    await alert.present();
    const { role } = await alert.onDidDismiss();
    if (role === 'confirm') this.state.signedInAgain();
    else this.state.setExpired(false);
    if (role === 'page') {
      // The unsaved-changes guard still asks before leaving an editor with edits.
      await this.router.navigate(['/auth/login'], { queryParams: { returnto: this.router.url } });
    }
  }

  private async signIn(values: { email?: string; password?: string }, alert: HTMLIonAlertElement): Promise<boolean> {
    const email = values.email?.trim();
    if (!email || !values.password) {
      alert.message = 'Enter your email and password.';
      return false;
    }
    const result = await this.auth.signIn(email, values.password);
    if (result.ok && result.user.role === Role.admin) {
      this.toasts.success('Signed in. Save again to keep your changes.');
      return true;
    }
    alert.message = result.ok
      ? 'That account isn’t an admin. Sign in with an admin account.'
      : result.needsNewPassword
        ? 'This account needs a new password: use the sign-in page.'
        : result.error;
    return false;
  }
}
